/**
 * Notifications inbox screen + Home slide-out panel (News-style).
 */
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type UserNotificationDto,
} from "../lib/cloud";

type NotificationsMount = {
  isSignedIn: () => boolean;
};

type SortFilter = "newest" | "oldest";

let mount: NotificationsMount | null = null;
let unreadCount = 0;
let cachedItems: UserNotificationDto[] = [];
let notificationsInHomeEnabled = true;
let homePanelOpen = false;
let sortFilter: SortFilter = "newest";

const KIND_LABELS: Record<string, string> = {
  message: "Message",
  gift: "Gift",
  payment: "Payment",
  leaderboard: "Ranking",
  achievement: "Achievement",
  shop: "Shop",
  daily_dots: "Dots",
  system: "System",
};

function formatWhen(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function sortedItems(): UserNotificationDto[] {
  const items = [...cachedItems];
  items.sort((a, b) => {
    const ta = Date.parse(a.createdAt) || 0;
    const tb = Date.parse(b.createdAt) || 0;
    return sortFilter === "oldest" ? ta - tb : tb - ta;
  });
  return items;
}

function syncUnreadBadge(): void {
  const badge = document.getElementById("homeNotificationsBadge");
  const sideBadge = document.getElementById("sideNotificationsBadge");
  const label = unreadCount > 99 ? "99+" : unreadCount > 0 ? String(unreadCount) : "";
  for (const el of [badge, sideBadge]) {
    if (!el) continue;
    el.textContent = label;
    el.classList.toggle("hidden", !label);
  }
}

function syncHomePanelPlacement(): void {
  const panel = document.getElementById("homeNotificationsPanel");
  const newsPanel = document.getElementById("homeNewsPanel");
  const startScreen = document.getElementById("startScreen");
  if (!panel || !startScreen) return;

  const onHome =
    notificationsInHomeEnabled &&
    !startScreen.classList.contains("hidden") &&
    !document.body.classList.contains("account-bootstrap-active");
  const newsVisible = !!(newsPanel && !newsPanel.classList.contains("hidden"));

  panel.classList.toggle("hidden", !onHome);
  panel.classList.toggle("homeNotificationsPanel--belowNews", onHome && newsVisible);
  panel.classList.toggle("homeNotificationsPanel--midRight", onHome && !newsVisible);
  if (!onHome) {
    homePanelOpen = false;
    panel.classList.remove("homeNotificationsPanel--open");
  }
}

function syncHomePanelOpenState(): void {
  const panel = document.getElementById("homeNotificationsPanel");
  const tab = document.getElementById("homeNotificationsPanelTab");
  if (!panel || !tab) return;
  panel.classList.toggle("homeNotificationsPanel--open", homePanelOpen);
  tab.setAttribute("aria-expanded", homePanelOpen ? "true" : "false");
  tab.setAttribute(
    "aria-label",
    homePanelOpen ? "Close notifications panel" : "Open notifications panel",
  );
}

export function syncNotificationsInHomeEnabled(on: boolean): void {
  notificationsInHomeEnabled = !!on;
  syncHomeNotificationsBell();
}

export function syncHomeNotificationsBell(): void {
  syncHomePanelPlacement();
  syncHomePanelOpenState();
}

async function refreshUnreadQuiet(): Promise<void> {
  if (!mount?.isSignedIn()) {
    unreadCount = 0;
    syncUnreadBadge();
    return;
  }
  try {
    const data = await fetchNotifications(1);
    unreadCount = data.unreadCount;
    syncUnreadBadge();
  } catch {
    /* ignore */
  }
}

function renderInto(
  list: HTMLElement | null,
  empty: HTMLElement | null,
  items: UserNotificationDto[],
): void {
  if (!list) return;
  list.replaceChildren();
  if (!items.length) {
    empty?.classList.remove("hidden");
    return;
  }
  empty?.classList.add("hidden");
  for (const n of items) {
    const li = document.createElement("li");
    li.className = "notificationsItem" + (n.readAt ? "" : " notificationsItem--unread");
    li.dataset.id = n.id;

    const kind = document.createElement("span");
    kind.className = "notificationsItemKind";
    kind.textContent = KIND_LABELS[n.kind] || n.kind;

    const title = document.createElement("strong");
    title.className = "notificationsItemTitle";
    title.textContent = n.title;

    const body = document.createElement("p");
    body.className = "notificationsItemBody";
    body.textContent = n.body;

    const meta = document.createElement("span");
    meta.className = "notificationsItemMeta";
    meta.textContent = formatWhen(n.createdAt);

    li.appendChild(kind);
    li.appendChild(title);
    li.appendChild(body);
    li.appendChild(meta);

    if (!n.readAt) {
      li.addEventListener("click", () => {
        void markNotificationRead(n.id)
          .then(() => {
            li.classList.remove("notificationsItem--unread");
            n.readAt = new Date().toISOString();
            unreadCount = Math.max(0, unreadCount - 1);
            syncUnreadBadge();
            // Keep other lists in sync
            renderAllLists();
          })
          .catch(() => {});
      });
    }
    list.appendChild(li);
  }
}

function renderAllLists(): void {
  const items = sortedItems();
  renderInto(
    document.getElementById("notificationsList"),
    document.getElementById("notificationsEmpty"),
    items,
  );
  renderInto(
    document.getElementById("homeNotificationsList"),
    document.getElementById("homeNotificationsEmpty"),
    items,
  );
}

function setStatus(text: string): void {
  for (const id of ["notificationsStatus", "homeNotificationsStatus"]) {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  }
}

export async function refreshNotificationsScreen(): Promise<void> {
  if (!mount?.isSignedIn()) {
    cachedItems = [];
    unreadCount = 0;
    syncUnreadBadge();
    renderAllLists();
    setStatus("Sign in to see notifications.");
    return;
  }
  setStatus("Loading…");
  try {
    const data = await fetchNotifications(100);
    cachedItems = data.items;
    unreadCount = data.unreadCount;
    syncUnreadBadge();
    renderAllLists();
    setStatus(data.items.length ? "" : "");
  } catch (e) {
    setStatus(e instanceof Error ? e.message : "Failed to load.");
  }
}

export async function refreshNotificationsUnread(): Promise<void> {
  await refreshUnreadQuiet();
}

function bindFilter(select: HTMLSelectElement | null): void {
  if (!select) return;
  select.value = sortFilter;
  select.addEventListener("change", () => {
    const v = select.value === "oldest" ? "oldest" : "newest";
    sortFilter = v;
    const other =
      select.id === "homeNotificationsFilter"
        ? document.getElementById("notificationsScreenFilter")
        : document.getElementById("homeNotificationsFilter");
    if (other instanceof HTMLSelectElement) other.value = v;
    renderAllLists();
  });
}

function bindMarkAll(btn: HTMLElement | null): void {
  if (!(btn instanceof HTMLButtonElement)) return;
  btn.onclick = () => {
    void markAllNotificationsRead()
      .then(() => {
        unreadCount = 0;
        for (const n of cachedItems) n.readAt = n.readAt || new Date().toISOString();
        syncUnreadBadge();
        renderAllLists();
        return refreshNotificationsScreen();
      })
      .catch(() => {});
  };
}

export function mountNotificationsUi(opts: NotificationsMount): void {
  mount = opts;

  bindMarkAll(document.getElementById("notificationsMarkAllBtn"));
  bindMarkAll(document.getElementById("homeNotificationsMarkAllBtn"));
  bindFilter(document.getElementById("homeNotificationsFilter") as HTMLSelectElement | null);
  bindFilter(document.getElementById("notificationsScreenFilter") as HTMLSelectElement | null);

  const tab = document.getElementById("homeNotificationsPanelTab");
  if (tab) {
    tab.addEventListener("click", () => {
      homePanelOpen = !homePanelOpen;
      syncHomePanelOpenState();
      if (homePanelOpen) void refreshNotificationsScreen();
    });
  }

  syncHomePanelPlacement();
  syncHomePanelOpenState();
  void refreshUnreadQuiet();
}

export function getNotificationsUnreadCount(): number {
  return unreadCount;
}
